// chat-modal.component.ts
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { User } from 'src/app/models/identity/user.model';
import { AccountsService } from 'src/app/services/identity/accounts.service';
import { ChatBridgeService } from 'src/app/services/shared/chat-bridge.service';
import { ChatFacadeService } from '../../services/chat-facade.service';
import { ConversationView } from '../../type/conversation-view.type';
import { Conversation } from '../../type/conversation.type';
import { Message } from '../../type/message.type';

@Component({
    selector: 'app-chat-modal',
    templateUrl: './chat-modal.component.html',
    styleUrls: ['./chat-modal.component.scss'],
})
export class ChatModalComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    // State from facade (reactive)
    conversations$: Observable<ConversationView[]> = this.chatFacade.conversations$;
    activeConversation$: Observable<Conversation | null> = this.chatFacade.activeConversation$;
    activeMessages$: Observable<Message[]> = this.chatFacade.activeMessages$;
    isLoading$: Observable<boolean> = this.chatFacade.isLoading$;
    targetUser$: Observable<User | null> = this.chatFacade.targetUser$;

    // UI state (local)
    modalOpen: boolean = false;
    isConversationHistory$: Observable<boolean>;
    filteredConversations: ConversationView[] = [];
    searchQuery: string = '';

    authenticatedUserId: string;

    constructor(
        private readonly chatFacade: ChatFacadeService,
        private readonly accountsService: AccountsService,
        private readonly chatBridgeService: ChatBridgeService,
    ) {
        this.authenticatedUserId = this.accountsService.currentUser.userId;
    }

    async ngOnInit() {
        // Subscribe to modal open state from bridge
        this.chatBridgeService.isChatModalOpen$.pipe(takeUntil(this.destroy$)).subscribe((isOpen) => {
            this.modalOpen = isOpen;
        });

        // Determine if showing history or conversation
        // Show conversation if activeConversationId is NOT null (including 'new' placeholder)
        // Show history if activeConversationId is null
        this.isConversationHistory$ = this.chatFacade.activeConversationId$.pipe(
            map((activeId) => {
                return activeId == null;
            }),
        );

        // Initialize chat and load conversations
        try {
            // Subscribe to conversations for local filtering
            this.conversations$.pipe(takeUntil(this.destroy$)).subscribe((conversations) => {
                this.filteredConversations = conversations;
            });

            // Handle external conversation opening AFTER conversations are loaded
            this.chatBridgeService.openConversation$
                .pipe(
                    takeUntil(this.destroy$),
                    // Set modalOpen, then open conversation (flatten inner observable)
                    switchMap((user: User) => {
                        this.modalOpen = true;
                        return this.chatFacade.openConversationWithUser(user);
                    }),
                )
                .subscribe();
        } catch (err) {
            console.error('Error initializing chat:', err);
        }
    }

    async ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    goBackToHistory(): void {
        this.chatFacade.goBackToHistory();
    }

    closeModal(): void {
        this.chatBridgeService.closeModal();
    }

    selectConversation(convView: ConversationView): void {
        this.chatFacade.selectConversationView(convView).subscribe();
    }

    onSearchChange(): void {
        this.conversations$.pipe(take(1)).subscribe((conversations) => {
            if (!this.searchQuery || this.searchQuery.trim() === '') {
                this.filteredConversations = conversations;
                return;
            }

            const query = this.searchQuery.toLowerCase().trim();
            this.filteredConversations = conversations.filter((convView) => {
                if (!convView.targetUser) return false;

                const fullName = `${convView.targetUser.firstName} ${convView.targetUser.lastName}`.toLowerCase();
                return fullName.includes(query);
            });
        });
    }

    onUserSelected(user: User): void {
        this.chatFacade.openConversationWithUser(user).subscribe();
    }

    onMessageSent(content: string): void {
        this.activeConversation$.pipe(take(1)).subscribe((conversation) => {
            if (conversation) {
                this.chatFacade.sendMessage(conversation.id, content);
            } else {
                // New conversation - need target user
                this.targetUser$.pipe(take(1)).subscribe((targetUser) => {
                    if (targetUser) {
                        this.chatFacade.startConversation(targetUser.userId, content);
                    }
                });
            }
        });
    }
}
